import React, { useEffect, useMemo, useState } from 'react';

import { typography } from '../../styles/typography';

import { employeeApi } from '../ShiftManagement/employeeApi';

import {
  DEFAULT_EMPLOYEE_CATEGORY_TAB,

  EMPLOYEE_CATEGORY_SECTION_LABELS,

  EMPLOYEE_CATEGORY_TABS,

  areAllFloorsCheckedForBlock,

  areAllFloorsFullySelectedForBlock,

  createEmptyCleaningAreaSelection,

  createEmptyEmployeeDescriptionsByCategory,

  createEmptySelectedEmployeesByCategory,

  filterEmployeesByCategory,

  getActiveBlockTab,

  getBlockFloors,

  getBlockIds,

  getFloorsForBlock,

  PERSONNEL_TYPE_BY_CATEGORY_TAB,

  getEmployeeDescriptionByCategory,
  getSelectedEmployeeIds,

  isFloorChecked,

  isFloorFullySelected,

  usesCollapsibleFloorTasks,

  usesSimpleFloorCheckboxes,

  syncFloorsByBlockForSelection,
} from './cleaningAreaData';

import CollapsibleCheckboxSection, { CHECKBOX_CLASS } from './CollapsibleCheckboxSection';

import EmployeeCheckboxList from './EmployeeCheckboxList';

import { validateEmployeesAvailability } from './employeeAvailabilityUtils';

import { getCleaningScheduleUsersForCategory } from './cleaningScheduleApi';

import UnderlineTabBar from './UnderlineTabBar';



const getEmployeeId = employee => employee?.user_id || employee?.employee_id;



const normalizeEmployee = (employee, source) => ({

  ...employee,

  _source: source,

  display_name: employee.user_name || employee.name || employee.employee_name || 'Unknown',

  role: employee.spec_role || employee.role_name || source,

  employee_id: employee.employee_id || employee.user_id,

});



const CleaningSelectArea = ({
  value,
  onChange,
  shiftValidationData = null,
  siteId = null,
  siteCatalog = null,
  isLoadingCatalog = false,
}) => {

  const blocks = useMemo(() => siteCatalog?.blocks ?? [], [siteCatalog]);

  const tasks = useMemo(() => siteCatalog?.tasks ?? [], [siteCatalog]);

  useEffect(() => {
    if (!siteId) return;
    console.log('[CleaningSelectArea] blocks for UI', {
      siteId,
      isLoadingCatalog,
      siteCatalogNull: siteCatalog == null,
      blockCount: blocks.length,
      blocks,
      tasksCount: tasks.length,
    });
  }, [siteId, siteCatalog, blocks, tasks, isLoadingCatalog]);

  const selection = value || createEmptyCleaningAreaSelection();

  const selectedBlockIds = getBlockIds(selection);

  const activeBlockTab = getActiveBlockTab(selection, selectedBlockIds);

  const selectedBlockTabs = useMemo(
    () =>
      selectedBlockIds
        .map(id => blocks.find(b => b.id === id))
        .filter(Boolean)
        .map(block => ({ id: block.id, label: block.label })),
    [selectedBlockIds, blocks]
  );

  const activeFloors = useMemo(() => {
    if (!activeBlockTab) return [];
    return getFloorsForBlock({ blocks }, activeBlockTab);
  }, [blocks, activeBlockTab]);

  const blockFloorsContext = blockId => ({
    floors: getFloorsForBlock({ blocks }, blockId),
    tasks,
  });

  const activeCategoryTab = selection.employeeCategoryTab || DEFAULT_EMPLOYEE_CATEGORY_TAB;

  const [expandedFloorId, setExpandedFloorId] = useState(null);

  const [allEmployees, setAllEmployees] = useState([]);

  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  const [employeesError, setEmployeesError] = useState(null);

  const [employeeStatuses, setEmployeeStatuses] = useState({});

  const [isValidatingAvailability, setIsValidatingAvailability] = useState(false);



  const updateSelection = next => {

    onChange?.(next);

  };



  useEffect(() => {

    let cancelled = false;



    const loadEmployees = async () => {

      setIsLoadingEmployees(true);

      setEmployeesError(null);

      try {

        let combined = [];

        if (siteId) {
          const siteUsers = await getCleaningScheduleUsersForCategory(
            siteId,
            activeCategoryTab
          );
          if (cancelled) return;
          combined = siteUsers.map(e =>
            normalizeEmployee(
              e,
              e._source === 'supervisor' ? 'supervisor' : 'guard'
            )
          );
        } else {
          const [guards, supervisors] = await Promise.all([
            employeeApi.fetchAvailableGuards().catch(() => []),
            employeeApi.fetchAvailableSupervisors().catch(() => []),
          ]);

          if (cancelled) return;

          combined = [
            ...(Array.isArray(supervisors) ? supervisors : []).map(e =>
              normalizeEmployee(e, 'supervisor')
            ),
            ...(Array.isArray(guards) ? guards : []).map(e =>
              normalizeEmployee(e, 'guard')
            ),
          ];
        }



        const uniqueById = new Map();

        combined.forEach(employee => {

          const id = getEmployeeId(employee);

          if (id && !uniqueById.has(id)) {

            uniqueById.set(id, employee);

          }

        });



        setAllEmployees(Array.from(uniqueById.values()));

      } catch (err) {

        if (!cancelled) {

          setEmployeesError(err.message || 'Failed to load employees');

          setAllEmployees([]);

        }

      } finally {

        if (!cancelled) {

          setIsLoadingEmployees(false);

        }

      }

    };



    loadEmployees();

    return () => {

      cancelled = true;

    };

  }, [siteId, activeCategoryTab]);



  useEffect(() => {

    let cancelled = false;



    const runValidation = async () => {

      if (!shiftValidationData?.date || allEmployees.length === 0) {

        if (!cancelled) setEmployeeStatuses({});

        return;

      }



      setIsValidatingAvailability(true);

      try {

        const statuses = await validateEmployeesAvailability(

          allEmployees,

          shiftValidationData

        );

        if (!cancelled) setEmployeeStatuses(statuses);

      } catch {

        if (!cancelled) setEmployeeStatuses({});

      } finally {

        if (!cancelled) setIsValidatingAvailability(false);

      }

    };



    runValidation();

    return () => {

      cancelled = true;

    };

  }, [allEmployees, shiftValidationData]);



  const categoryEmployees = useMemo(() => {
    if (siteId && PERSONNEL_TYPE_BY_CATEGORY_TAB[activeCategoryTab]) {
      return allEmployees;
    }
    return filterEmployeesByCategory(allEmployees, activeCategoryTab);
  }, [allEmployees, activeCategoryTab, siteId]);



  const selectedEmployeeIds = getSelectedEmployeeIds(selection, activeCategoryTab);



  const selectedEmployeesByCategory =

    selection.selectedEmployeeIdsByCategory || createEmptySelectedEmployeesByCategory();



  const employeeDescriptionsByCategory =

    selection.employeeDescriptionsByCategory || createEmptyEmployeeDescriptionsByCategory();



  const activeCategoryLabel =

    EMPLOYEE_CATEGORY_TABS.find(tab => tab.id === activeCategoryTab)?.label || '';



  const categoryEmployeeDescription = getEmployeeDescriptionByCategory(

    selection,

    activeCategoryTab

  );



  const updateEmployeeDescriptionForCategory = (categoryId, description) => {

    updateSelection({

      ...selection,

      employeeDescriptionsByCategory: {

        ...employeeDescriptionsByCategory,

        [categoryId]: description,

      },

    });

  };



  const updateSelectedEmployeesForCategory = (categoryId, ids) => {

    updateSelection({

      ...selection,

      selectedEmployeeIdsByCategory: {

        ...selectedEmployeesByCategory,

        [categoryId]: ids,

      },

    });

  };



  const toggleEmployee = employee => {

    const employeeId = getEmployeeId(employee);

    const nextIds = selectedEmployeeIds.includes(employeeId)

      ? selectedEmployeeIds.filter(id => id !== employeeId)

      : [...selectedEmployeeIds, employeeId];

    updateSelectedEmployeesForCategory(activeCategoryTab, nextIds);

  };



  const setAllCategoryEmployees = (checked, filteredList) => {

    const filteredIds = filteredList.map(getEmployeeId).filter(Boolean);

    const nextIds = checked

      ? [...new Set([...selectedEmployeeIds, ...filteredIds])]

      : selectedEmployeeIds.filter(id => !filteredIds.includes(id));

    updateSelectedEmployeesForCategory(activeCategoryTab, nextIds);

  };



  const updateFloor = (floorId, patch) => {

    const blockId = getActiveBlockTab(selection, selectedBlockIds);

    if (!blockId) return;

    const blockFloors = getBlockFloors(selection, blockId, blockFloorsContext(blockId));

    updateSelection({

      ...selection,

      floorsByBlock: {

        ...(selection.floorsByBlock || {}),

        [blockId]: {

          ...blockFloors,

          [floorId]: {

            ...blockFloors[floorId],

            ...patch,

          },

        },

      },

    });

  };



  const toggleFloorSelected = floorId => {

    const blockId = getActiveBlockTab(selection, selectedBlockIds);

    const floorState = getBlockFloors(selection, blockId, blockFloorsContext(blockId))[
      floorId
    ];

    updateFloor(floorId, { selected: !isFloorChecked(floorState) });

  };



  const setAllFloorsSelected = checked => {

    const blockId = getActiveBlockTab(selection, selectedBlockIds);

    if (!blockId) return;

    const blockFloors = {
      ...getBlockFloors(selection, blockId, blockFloorsContext(blockId)),
    };

    activeFloors.forEach(floor => {

      blockFloors[floor.id] = {

        ...blockFloors[floor.id],

        selected: checked,

      };

    });

    updateSelection({

      ...selection,

      floorsByBlock: {

        ...(selection.floorsByBlock || {}),

        [blockId]: blockFloors,

      },

    });

  };



  const setAllFloorsTasks = checked => {

    const blockId = getActiveBlockTab(selection, selectedBlockIds);

    if (!blockId) return;

    const blockFloors = {
      ...getBlockFloors(selection, blockId, blockFloorsContext(blockId)),
    };

    activeFloors.forEach(floor => {

      blockFloors[floor.id] = {

        ...blockFloors[floor.id],

        tasks: tasks.reduce((acc, task) => {

          acc[task.id] = checked;

          return acc;

        }, {}),

      };

    });

    updateSelection({

      ...selection,

      floorsByBlock: {

        ...(selection.floorsByBlock || {}),

        [blockId]: blockFloors,

      },

    });

  };



  const setFloorTasks = (floorId, checked) => {

    const nextTasks = tasks.reduce((acc, task) => {

      acc[task.id] = checked;

      return acc;

    }, {});

    updateFloor(floorId, { tasks: nextTasks });

  };



  const toggleTask = (floorId, taskId) => {

    const blockId = getActiveBlockTab(selection, selectedBlockIds);

    const floorState = getBlockFloors(selection, blockId, blockFloorsContext(blockId))[
      floorId
    ];

    updateFloor(floorId, {

      tasks: {

        ...floorState.tasks,

        [taskId]: !floorState.tasks[taskId],

      },

    });

  };



  const applyBlockIdsUpdate = nextBlockIds => {

    const { blockId: _legacy, floors: _legacyFloors, ...rest } = selection;

    const { floorsByBlock, activeBlockTab: nextActiveBlockTab } =

      syncFloorsByBlockForSelection(selection, nextBlockIds, { blocks, tasks });

    updateSelection({

      ...rest,

      blockIds: nextBlockIds,

      floorsByBlock,

      activeBlockTab: nextActiveBlockTab,

    });

  };



  const toggleBlock = blockId => {

    const nextBlockIds = selectedBlockIds.includes(blockId)

      ? selectedBlockIds.filter(id => id !== blockId)

      : [...selectedBlockIds, blockId];

    applyBlockIdsUpdate(nextBlockIds);

  };



  const setAllBlocks = checked => {

    applyBlockIdsUpdate(checked ? blocks.map(b => b.id) : []);

  };



  const allBlocksSelected =

    blocks.length > 0 &&

    blocks.every(block => selectedBlockIds.includes(block.id));



  const showCollapsibleFloors = usesCollapsibleFloorTasks(activeCategoryTab);

  const showSimpleFloorCheckboxes = usesSimpleFloorCheckboxes(activeCategoryTab);



  const allFloorsTasksSelected = areAllFloorsFullySelectedForBlock(
    selection,
    activeBlockTab,
    activeFloors,
    tasks
  );

  const allFloorsCheckboxSelected = areAllFloorsCheckedForBlock(
    selection,
    activeBlockTab,
    activeFloors
  );



  const sectionLabel =

    EMPLOYEE_CATEGORY_SECTION_LABELS[activeCategoryTab] || 'Employees';



  const showFloorsPanel = activeCategoryTab !== 'facility-team';



  return (

    <div className="self-stretch flex flex-col gap-4">

      <h3 className={typography.h3} style={{ color: '#000000' }}>

        Select Area

      </h3>

      {isLoadingCatalog && (
        <p className="text-sm text-gray-500 font-['Open_Sans']">Loading site blocks and tasks…</p>
      )}

      {!isLoadingCatalog && siteId && siteCatalog != null && blocks.length === 0 && (
        <p className="text-sm text-amber-800 font-['Open_Sans']">
          No blocks or streets are configured for this site. Add them under site
          facility setup (blocks/flats bulk create) so they appear in community
          schedule locations.
        </p>
      )}

      {/* Block / Street — multi-select */}

      <div className="flex flex-col gap-2 w-full">

        <div className="flex items-center justify-between w-full">

          <span className="text-base font-semibold text-black font-['Open_Sans']">

            Block / Street

          </span>

          <label className="flex items-center gap-2 cursor-pointer">

            <input

              type="checkbox"

              className={CHECKBOX_CLASS}

              checked={allBlocksSelected}

              onChange={e => setAllBlocks(e.target.checked)}

            />

            <span className="text-sm text-gray-500 font-['Open_Sans']">Select all</span>

          </label>

        </div>

        <div className="flex flex-col gap-2">

          {blocks.map(block => (

            <label

              key={block.id}

              className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white border border-zinc-200 cursor-pointer hover:border-[#6BABA0]/50 transition-colors"

            >

              <input

                type="checkbox"

                className={CHECKBOX_CLASS}

                checked={selectedBlockIds.includes(block.id)}

                onChange={() => toggleBlock(block.id)}

              />

              <span className="text-base text-black font-['Open_Sans']">{block.label}</span>

            </label>

          ))}

        </div>

      </div>



      {/* Select employee — category tabs */}

      <UnderlineTabBar

        title="Select employee"

        tabs={EMPLOYEE_CATEGORY_TABS}

        activeTabId={activeCategoryTab}

        onTabChange={tabId => updateSelection({ ...selection, employeeCategoryTab: tabId })}

      />



      {/* Employees (left) + Floors (right) — floors hidden for Facility team */}

      <div
        className={`grid gap-4 lg:gap-6 w-full items-start ${
          showFloorsPanel ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
        }`}
      >

        <div className="flex flex-col gap-3 w-full min-w-0">

          <EmployeeCheckboxList

            title={sectionLabel}

            employees={categoryEmployees}

            selectedIds={selectedEmployeeIds}

            onToggleEmployee={toggleEmployee}

            onSelectAll={setAllCategoryEmployees}

            isLoading={isLoadingEmployees}

            isValidatingAvailability={isValidatingAvailability}

            employeeStatuses={employeeStatuses}

            error={employeesError}

            emptyMessage={`No ${sectionLabel.toLowerCase()} found`}

          />



          {selectedEmployeeIds.length > 0 && (

            <div className="flex flex-col gap-2">

              <span className="text-sm font-medium text-black font-['Open_Sans']">

                Description ({activeCategoryLabel})

              </span>

              <p className="text-xs text-gray-500 font-['Open_Sans']">

                For all selected employees in {activeCategoryLabel}

              </p>

              <textarea

                value={categoryEmployeeDescription}

                onChange={e =>

                  updateEmployeeDescriptionForCategory(activeCategoryTab, e.target.value)

                }

                placeholder={`Enter description for selected ${activeCategoryLabel} employees...`}

                rows={3}

                className="w-full px-4 py-3 rounded-xl bg-white border border-zinc-300 text-base text-black font-['Open_Sans'] placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#6BABA0]/40 focus:border-[#6BABA0]"

              />

            </div>

          )}

        </div>



        {showFloorsPanel && (

        <div className="flex flex-col gap-3 w-full min-w-0">

          {selectedBlockTabs.length > 0 ? (

            <>

              <UnderlineTabBar

                tabs={selectedBlockTabs}

                activeTabId={activeBlockTab}

                onTabChange={tabId => {

                  setExpandedFloorId(null);

                  updateSelection({ ...selection, activeBlockTab: tabId });

                }}

              />

              <div className="flex items-center justify-between w-full">

                <span className="text-base font-semibold text-black font-['Open_Sans']">

                  Floors

                </span>

                <label className="flex items-center gap-2 cursor-pointer">

                  <input

                    type="checkbox"

                    className={CHECKBOX_CLASS}

                    checked={

                      showSimpleFloorCheckboxes

                        ? allFloorsCheckboxSelected

                        : allFloorsTasksSelected

                    }

                    onChange={e =>

                      showSimpleFloorCheckboxes

                        ? setAllFloorsSelected(e.target.checked)

                        : setAllFloorsTasks(e.target.checked)

                    }

                  />

                  <span className="text-sm text-gray-500 font-['Open_Sans']">Select all</span>

                </label>

              </div>



              {showCollapsibleFloors && (

              <div className="flex flex-col gap-3 w-full">

                {activeFloors.map(floor => {

                  const floorState = getBlockFloors(
                    selection,
                    activeBlockTab,
                    blockFloorsContext(activeBlockTab)
                  )[floor.id];

                  const isExpanded = expandedFloorId === floor.id;



              return (

                <CollapsibleCheckboxSection

                  key={floor.id}

                  title={floor.label}

                  isExpanded={isExpanded}

                  onToggle={() => setExpandedFloorId(isExpanded ? null : floor.id)}

                  items={tasks}

                  selectedMap={floorState?.tasks || {}}

                  onToggleItem={taskId => toggleTask(floor.id, taskId)}

                  onSelectAll={checked => setFloorTasks(floor.id, checked)}

                  allSelected={isFloorFullySelected(floorState, tasks)}

                  description={floorState?.description || ''}

                  onDescriptionChange={value => updateFloor(floor.id, { description: value })}

                />

              );

            })}

              </div>

              )}



              {showSimpleFloorCheckboxes && (

              <div className="flex flex-col gap-2 w-full">

                {activeFloors.map(floor => {

                  const floorState = getBlockFloors(
                    selection,
                    activeBlockTab,
                    blockFloorsContext(activeBlockTab)
                  )[floor.id];

                  const isSelected = isFloorChecked(floorState);



                  return (

                    <label

                      key={floor.id}

                      className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white border border-zinc-200 cursor-pointer hover:border-[#6BABA0]/50 transition-colors"

                    >

                      <input

                        type="checkbox"

                        className={CHECKBOX_CLASS}

                        checked={isSelected}

                        onChange={() => toggleFloorSelected(floor.id)}

                      />

                      <span className="text-base text-black font-['Open_Sans']">

                        {floor.label}

                      </span>

                    </label>

                  );

                })}

              </div>

              )}

            </>

          ) : (

            <p className="text-sm text-gray-500 font-['Open_Sans'] py-2">

              Select one or more Block / Street above to configure floors.

            </p>

          )}

        </div>

        )}

      </div>

    </div>

  );

};



export default CleaningSelectArea;

